import React, { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

import {
  generateHeatmapData,
  getHeatmapGradient,
  getHeatmapRadius,
  getHeatmapBlur,
  getHeatmapMax,
  getHeatmapMin
} from '../../utils/heatmapUtils';

export function HeatmapLayer({ stations, dataType, visible = false, opacity = 0.75, onValuesChange, onCalculatingChange }) {
  const map = useMap();
  const layerRef = React.useRef(null);

  // Calculate min/max values from station data for better normalization
  const { heatmapData, minValue, maxValue } = useMemo(() => {
    if (!stations || stations.length === 0) {
      return { heatmapData: [], minValue: 0, maxValue: 0 };
    }

    // Get actual values from stations (not from interpolated points)
    const stationValues = [];
    stations.forEach(station => {
      if (!station.currentReadings || !station.isActive) return;
      
      let value = 0;
      switch (dataType) {
        case 'temperature':
          value = station.currentReadings.temperature || 0;
          break;
        case 'windSpeed':
          value = station.currentReadings.windSpeed || 0;
          break;
        case 'windDirection':
          value = station.currentReadings.windSpeed || 0;
          break;
        case 'humidity':
          value = station.currentReadings.humidity || 0;
          break;
        case 'solarRadiation':
          value = (station.currentReadings.solarRadiation || 0) / 10;
          break;
        default:
          return;
      }
      
      if (value > 0) {
        stationValues.push(value);
      }
    });

    if (stationValues.length === 0) {
      return { heatmapData: [], minValue: 0, maxValue: 0 };
    }

    // Calculate min/max from actual station values
    const dataMin = Math.min(...stationValues);
    const dataMax = Math.max(...stationValues);
    
    // Use configured min/max as bounds, but allow data to determine actual range
    const configMin = getHeatmapMin(dataType);
    const configMax = getHeatmapMax(dataType);
    
    const minValue = Math.max(configMin, dataMin * 0.95); // Less padding to show differences
    const maxValue = Math.min(configMax || dataMax * 1.05, dataMax * 1.05); // Less padding
    
    // Generate station-based heatmap data (individual blobs for each station)
    const data = generateHeatmapData(stations, dataType, false);
    
    return { heatmapData: data, minValue, maxValue };
  }, [stations, dataType]);

  useEffect(() => {
    if (!visible || !stations || stations.length === 0 || heatmapData.length === 0) {
      // Remove layer if not visible
      if (layerRef.current) {
        try {
          if (map.hasLayer(layerRef.current)) {
            map.removeLayer(layerRef.current);
          }
        } catch (error) {
          console.debug('Error removing heatmap layer:', error);
        }
        layerRef.current = null;
      }
      return;
    }

    // Check if map is ready
    if (!map || !map.getContainer()) {
      return;
    }

    // Clean up existing layer first
    if (layerRef.current) {
      try {
        if (map.hasLayer(layerRef.current)) {
          map.removeLayer(layerRef.current);
        }
      } catch (error) {
        console.debug('Error removing existing heatmap layer:', error);
      }
      layerRef.current = null;
    }

    // OPTIMIZED: Debounce heatmap creation for better performance
    const createHeatmap = () => {
      // Double-check map is still valid
      if (!map || !map.getContainer()) {
        return;
      }

      // Notify that calculation is starting
      if (onCalculatingChange) {
        onCalculatingChange(true);
      }

      try {
        // Get configuration
        const gradient = getHeatmapGradient(dataType);
        // Keep radius smaller to create isolated circular blobs around each station
        // Higher blur creates soft edges without angular shapes
        const radius = getHeatmapRadius(dataType) * 0.8; // Smaller radius for isolated blobs
        const blur = radius * 0.9; // High blur relative to radius for soft circular edges

        // Create heatmap layer with settings optimized for circular station-based visualization
        const heatmapLayer = L.heatLayer(heatmapData, {
          radius: radius,
          blur: blur,
          maxZoom: 20, // Allow heatmap at higher zoom levels
          minZoom: 0,
          max: maxValue, // Use calculated max for better normalization
          gradient: gradient,
          opacity: opacity * 0.85 // Slightly transparent
        });

        // Override _redraw to add safety checks
        const originalRedraw = heatmapLayer._redraw;
        heatmapLayer._redraw = function() {
          try {
            if (this._map && this._map.getContainer && this._map.getContainer()) {
              originalRedraw.call(this);
            }
          } catch (error) {
            console.debug('Error in heatmap redraw:', error);
          }
        };

        // Add to map with error handling
        try {
          // Add to overlayPane (below markerPane) to ensure markers are visible
          if (map.getPane('overlayPane')) {
            heatmapLayer.addTo(map);
            layerRef.current = heatmapLayer;
            
            // Ensure heatmap is below markers by setting z-index on the canvas
            // Heatmap layers use canvas, so we need to access the renderer
            const setZIndex = () => {
              if (heatmapLayer._renderer && heatmapLayer._renderer._container) {
                heatmapLayer._renderer._container.style.zIndex = '200';
                heatmapLayer._renderer._container.style.pointerEvents = 'none'; // Allow clicks to pass through
              }
              // Also ensure marker pane is above
              const markerPane = map.getPane('markerPane');
              if (markerPane) {
                markerPane.style.zIndex = '600';
              }
            };
            
            // Try multiple times to ensure it's set
            setZIndex();
            setTimeout(setZIndex, 50);
            setTimeout(setZIndex, 200);
            setTimeout(setZIndex, 500);
          } else {
            heatmapLayer.addTo(map);
            layerRef.current = heatmapLayer;
          }
          
          // Notify that calculation is complete
          if (onCalculatingChange) {
            setTimeout(() => onCalculatingChange(false), 100);
          }
        } catch (error) {
          console.debug('Error adding heatmap layer to map:', error);
          layerRef.current = null;
          if (onCalculatingChange) {
            onCalculatingChange(false);
          }
        }
      } catch (error) {
        console.debug('Error creating heatmap layer:', error);
        layerRef.current = null;
        if (onCalculatingChange) {
          onCalculatingChange(false);
        }
      }
    };

    // Use a small delay to ensure map is fully ready
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(createHeatmap);
    }, 50);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (layerRef.current) {
        try {
          if (map && map.hasLayer && map.hasLayer(layerRef.current)) {
            map.removeLayer(layerRef.current);
          }
        } catch (error) {
          console.debug('Error cleaning up heatmap layer:', error);
        }
        layerRef.current = null;
      }
    };
  }, [map, stations, dataType, visible, opacity, heatmapData, maxValue]);

  // Expose min/max values for legend via callback
  // Use ref to track previous values to avoid infinite loops
  const prevValuesRef = React.useRef({ min: null, max: null });
  
  React.useEffect(() => {
    if (visible && onValuesChange && minValue !== undefined && maxValue !== undefined) {
      // Only call callback if values actually changed
      if (prevValuesRef.current.min !== minValue || prevValuesRef.current.max !== maxValue) {
        prevValuesRef.current = { min: minValue, max: maxValue };
        onValuesChange({ min: minValue, max: maxValue });
      }
    }
  }, [visible, minValue, maxValue]); // Removed onValuesChange from dependencies

  return null;
}


